import React from "react";
import { Box, Flex, Text, Icon, Stack } from "@chakra-ui/react";
import InputSearch from "@components/Sales/Inputs/InputSearch";
import salesService from "@services/Sales/SalesService";
import Alert from "@components/common/Alert";
import { LoginContext } from "@context/loginContext";
import TableMyOportunity from "@components/Sales/TableMyOportunity";
import InputSelect from "@components/Sales/Inputs/InputSelect";
import { optionFase, optionOriginAccountAcronyms } from "@helpers/optionSelect";
import Spinner from "@components/common/Spinner";
import { formatDatePtBr } from "@helpers/format";
import InputText from "@components/Sales/Inputs/InputText";
import Button from "@components/common/Button";
import { RxUpdate } from "react-icons/rx";
import type { TableRow } from "src/@types/Sales";
import { theme } from "src/styles/theme";
import { OportunityContext } from "@context/oportunityContex";
import useWindowDimensions from "@hook/useWindowDimensions";
import { DEFAULT_PHASES } from "@helpers/defaultValues";

const MyOportunityComponent = () => {
  const { setIndexTabDetails } = React.useContext(OportunityContext);

  const handleNavigation = () => {
    setIndexTabDetails(0);
  };

  const MyOportunityComponentOriginalWrapper = ({ navigation }: any) => {
    const idCorretor: any = localStorage.getItem("@idCorretor");
    const { signOut } = React.useContext(LoginContext);
    const { faseMyOportunity: faseFromContext, setFaseMyOportunity } =
      React.useContext(OportunityContext);
    const [faseMyOportunityLocal, setFaseMyOportunityLocal] = React.useState<
      string[] | string
    >(() => (Array.isArray(faseFromContext) ? faseFromContext : []));
    const [isUnauthorized, setIsUnauthorized] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [dataOportunity, setDataOportunity] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [newSearch, setNewSearch] = React.useState("");
    const [originAccount, setOriginAccount] = React.useState<string | string[]>(
      []
    );

    const [dateStart, setDateStart] = React.useState("");
    const [dateEnd, setDateEnd] = React.useState("");
    const [filtersLoaded, setFiltersLoaded] = React.useState(false);

    const { width } = useWindowDimensions();
    const isMobile = width < 768; // 768px é o breakpoint md padrão do Chakra UI

    const fases = React.useMemo(() => {
      const fromLocal = Array.isArray(faseMyOportunityLocal)
        ? faseMyOportunityLocal
        : [faseMyOportunityLocal];

      const fromContext = Array.isArray(faseFromContext)
        ? faseFromContext
        : [faseFromContext];

      const isFromContext =
        fromLocal.length === fromContext.length &&
        fromLocal.every((f) => fromContext.includes(f));

      return isFromContext ? fromContext : fromLocal;
    }, [faseMyOportunityLocal, faseFromContext]);

    const faseToDisplay = React.useMemo(() => {
      const local = Array.isArray(faseMyOportunityLocal)
        ? faseMyOportunityLocal
        : [faseMyOportunityLocal];

      const context = Array.isArray(faseFromContext)
        ? faseFromContext
        : [faseFromContext];

      const isFromContext =
        local.length === context.length &&
        local.every((f) => context.includes(f));

      return isFromContext ? context : local;
    }, [faseMyOportunityLocal, faseFromContext]);

    const handleGetOportunity = React.useCallback(async () => {
      setLoading(true);

      const handleNumberFase = () => {
        if (
          fases.some((f) => f.toLowerCase().includes("oportunidades vencidas"))
        )
          return 1;
        if (
          fases.some((f) => f.toLowerCase().includes("aguardando atendimento"))
        )
          return 2;
        if (fases.some((f) => f.toLowerCase().includes("contrato"))) return 4;
        if (fases.some((f) => f.toLowerCase().includes("cliente ouro")))
          return 5;
        return 3;
      };

      const handleNumberRank = () => {
        if (fases.some((f) => f.toLowerCase().includes("bronze"))) return 1;
        if (fases.some((f) => f.toLowerCase().includes("prata"))) return 2;
        if (fases.some((f) => f.toLowerCase().includes("ouro"))) return 3;
        if (fases.some((f) => f.toLowerCase().includes("diamante"))) return 4;
        if (fases.some((f) => f.toLowerCase().includes("aço"))) return 5;
        if (fases.some((f) => f.toLowerCase().includes("não elegível")))
          return 6;
        return null;
      };

      const isFilterFase =
        fases.some((f) => f.toLowerCase().includes("oportunidades vencidas")) ||
        fases.includes("OP's Cliente Ouro") ||
        fases.includes("Contrato");

      const filter = isFilterFase ? handleNumberFase() : undefined;

      try {
        const response = await salesService.getOportunity({
          idCorretorSalesforce: idCorretor,
          DataInicio: dateStart,
          DataFim: dateEnd,
          filtro: filter,
          rank: handleNumberRank(),
          filtroOrigemConta: originAccount,
        });

        if (response.status === 401) {
          setIsUnauthorized(true);
          setDataOportunity([]);
          setLoading(false);
          return;
        }

        if (response.status === 400) {
          setDataOportunity([]);
          setLoading(false);
          return;
        }

        setDataOportunity(Array.isArray(response) ? response : []);
        setNewSearch(search);
        setLoading(false);
      } catch (error) {
        console.log("errorHandleGetSales", error);
        setLoading(false);
      }
    }, [idCorretor, dateStart, dateEnd, setNewSearch, originAccount]);

    const handleAdjustNameFase = (faseMyOportunity: string | string[]) => {
      if (Array.isArray(faseMyOportunity)) {
        // Trata o caso de múltiplos selecionados e aplica o mesmo tratamento para "oportunidades vencidas"
        return faseMyOportunity
          .map((fase) =>
            fase.toLowerCase().includes("oportunidades vencidas")
              ? fase.split("-")[0]?.trim()
              : fase
          )
          .join(", ");
      } else {
        if (faseMyOportunity.toLowerCase().includes("oportunidades vencidas"))
          return faseMyOportunity.split("-")[0]?.trim();
        return faseMyOportunity;
      }
    };

    const handleAdjustName = (value: string | string[]) => {
      if (Array.isArray(value)) {
        return value.map((fase) => fase).join(", ");
      } else {
        return value;
      }
    };

    // Para search
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearch(value);
      localStorage.setItem("@myOpp_search", value);
    };

    // Para fase
    const handleFaseChange = (
      _e: React.ChangeEvent<HTMLInputElement>,
      selected: string[] | string
    ) => {
      let updatedFases: string[] = [];

      if (typeof selected === "string") {
        updatedFases = [selected];
      } else {
        const hasTodas = selected.includes("Todas");
        const prevHasTodas = faseMyOportunityLocal.includes("Todas");

        if (hasTodas && !prevHasTodas) {
          // Selecionou "Todas" -> marca todas
          updatedFases = optionFase.map((option) => option.name);
        } else if (!hasTodas && prevHasTodas) {
          // Estava em "Todas" e desmarcou alguma -> remove "Todas" e mantém só as outras
          updatedFases = selected.filter((f) => f !== "Todas");
        } else {
          // Seleção normal
          updatedFases = selected.filter((f) => f !== "Todas"); // <- aqui remove sempre "Todas" quando não for caso especial
        }
      }

      setFaseMyOportunityLocal(updatedFases);
      localStorage.setItem("@myOpp_fase", JSON.stringify(updatedFases));
    };

    // Para Origem da Conta
    const handleOriginAccountChange = (
      _e: React.ChangeEvent<HTMLInputElement>,
      selected: string | string[]
    ) => {
      setOriginAccount(selected);
      localStorage.setItem("@myOpp_originAccount", JSON.stringify(selected));
    };

    // Para datas
    const handleDateStartChange = (value: string) => {
      const formatted = formatDatePtBr(value);
      setDateStart(formatted);
      localStorage.setItem("@myOpp_dateStart", formatted);
    };

    const handleDateEndChange = (value: string) => {
      const formatted = formatDatePtBr(value);
      setDateEnd(formatted);
      localStorage.setItem("@myOpp_dateEnd", formatted);
    };

    const handleClearFilters = () => {
      setSearch("");
      setFaseMyOportunityLocal(DEFAULT_PHASES);
      setOriginAccount([]);
      setDateStart("");
      setDateEnd("");
      localStorage.removeItem("@myOpp_search");
      localStorage.setItem("@myOpp_fase", JSON.stringify(DEFAULT_PHASES));
      localStorage.removeItem("@myOpp_originAccount");
      localStorage.removeItem("@myOpp_dateStart");
      localStorage.removeItem("@myOpp_dateEnd");
    };

    const filteredRows = React.useMemo(() => {
      if (!dataOportunity) return [];

      const fases = Array.isArray(faseMyOportunityLocal)
        ? faseMyOportunityLocal
        : [faseMyOportunityLocal];

      const isRankingOrContrato =
        fases.some((f) =>
          f?.toLowerCase()?.includes("oportunidades vencidas")
        ) ||
        fases.some((f) => f?.toLowerCase()?.includes("diamante")) ||
        fases.some((f) => f?.toLowerCase()?.includes("ouro")) ||
        fases.some((f) => f?.toLowerCase()?.includes("prata")) ||
        fases.some((f) => f?.toLowerCase()?.includes("bronze")) ||
        fases.some((f) => f?.toLowerCase()?.includes("aço")) ||
        fases.some((f) => f?.toLowerCase()?.includes("não elegível")) ||
        fases.some((f) =>
          f?.toLowerCase()?.includes("ranking não disponível")
        ) ||
        fases.some((f) => f?.toLowerCase()?.includes("contrato"));

      if (isRankingOrContrato) {
        return dataOportunity;
      }

      return dataOportunity?.filter((row: TableRow) => {
        const matchesSearch =
          row?.nomeDaConta
            ?.toLowerCase()
            ?.includes(newSearch?.toLowerCase() || "") ||
          row?.opportunityIdSalesforce
            ?.toLowerCase()
            ?.includes(newSearch?.toLowerCase() || "");

        const rowFase = row?.fase?.toLowerCase() || "";

        const matchesFase =
          fases.length === 0 ||
          fases.includes("Todas") ||
          fases.some((faseItem) => faseItem?.toLowerCase() === rowFase);

        return matchesSearch && matchesFase;
      });
    }, [dataOportunity, faseMyOportunityLocal, newSearch]);

    React.useEffect(() => {
      const storedSearch = localStorage.getItem("@myOpp_search");
      const storedFase = localStorage.getItem("@myOpp_fase");
      const storedOriginAccount = localStorage.getItem("@myOpp_originAccount");
      const storedDateStart = localStorage.getItem("@myOpp_dateStart");
      const storedDateEnd = localStorage.getItem("@myOpp_dateEnd");

      if (storedSearch) setSearch(storedSearch);
      if (storedOriginAccount)
        setOriginAccount(JSON.parse(storedOriginAccount));
      if (storedDateStart) setDateStart(storedDateStart);
      if (storedDateEnd) setDateEnd(storedDateEnd);

      const parsedFase: string[] = storedFase
        ? JSON.parse(storedFase)
        : DEFAULT_PHASES;

      const local = Array.isArray(faseMyOportunityLocal)
        ? faseMyOportunityLocal
        : [faseMyOportunityLocal];

      const context = Array.isArray(faseFromContext)
        ? faseFromContext
        : [faseFromContext];

      const isFromContext =
        local.length === context.length &&
        local.every((f) => context.includes(f));

      if (context.length === 0) {
        setFaseMyOportunityLocal((prev) => {
          if (!Array.isArray(prev)) return parsedFase;

          const isSame =
            prev.length === parsedFase.length &&
            prev.every((val, idx) => val === parsedFase[idx]);

          return isSame ? prev : parsedFase;
        });
      }

      setFiltersLoaded(true);
    }, [faseFromContext, faseMyOportunityLocal]);

    React.useEffect(() => {
      if (
        filtersLoaded &&
        idCorretor &&
        ((dateStart.length === 10 && dateEnd.length === 10) ||
          (dateStart.length === 0 && dateEnd.length === 0))
      ) {
        handleGetOportunity();
      }
    }, [filtersLoaded, idCorretor, dateStart, dateEnd, handleGetOportunity]);

    return (
      <>
        <Alert
          onOpen={isUnauthorized}
          title="Sessão expirada"
          titleButton={"Fazer login novamente"}
          description={
            "Sua sessão expirou, faça login novamante para ter acesso."
          }
          onClick={() => {
            signOut();
          }}
        />

        {/* Header */}
        <Flex
          direction={{ base: "column", md: "row" }}
          w="99%"
          h={{ base: undefined, md: "80px" }}
          alignItems="center"
          justifyContent="space-between"
          gap={{ base: 4, md: 0 }}
          mt={{ base: "20px", md: "0px" }}
          px={{ base: 2, md: 0 }}
        >
          <Box
            display="flex"
            flexDir="column"
            alignItems={isMobile ? "flex-start" : "center"}
          >
            <Text fontSize={28} fontWeight="semibold" color={theme.text}>
              Minhas oportunidades
            </Text>
            <Text fontSize={14} color={theme.text50}>
              Selecione uma oportunidade para ver mais detalhes, criar
              agendamentos e mais
            </Text>
          </Box>

          <Flex
            gap={"6px"}
            direction={{ base: "column", sm: "row" }}
            w={{ base: "full", md: 220 }}
          >
            <Button
              title={
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap="4px"
                >
                  Limpar Filtros
                </Box>
              }
              onClick={handleClearFilters}
            />

            <Button
              title={
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap="4px"
                >
                  <Icon fontSize="16px" color="white" as={RxUpdate} />
                  Atualizar
                </Box>
              }
              onClick={() => handleGetOportunity()}
            />
          </Flex>
        </Flex>

        {/* Filtros */}
        <Flex
          flexDir="column"
          w="99%"
          h={isMobile ? "auto" : "120px"}
          bg="white"
          borderRadius="8px"
          mt="8px"
          gap="6px"
          p="10px"
          alignItems="center"
          justifyContent="center"
        >
          <Flex>
            <Text fontWeight="semibold">Pesquisa e filtros</Text>
          </Flex>

          <Flex
            gap="6px"
            flexWrap={isMobile ? "wrap" : "nowrap"}
            flexDirection={isMobile ? "column" : "row"}
            width="100%"
          >
            <Box
              display="flex"
              width={isMobile ? "100%" : "80%"}
              flexDirection={isMobile ? "column" : "row"}
              alignItems={isMobile ? "baseline" : "center"}
            >
              <InputSearch
                isOportunity
                label="Nome do cliente ou ID da oportunidade"
                placeholder={`Pesquisar...`}
                value={search}
                onChange={(e) => {
                  handleSearchChange(e);
                }}
              />
            </Box>

            <Flex
              width={isMobile ? "100%" : "95%"}
              gap="6px"
              flexDirection={isMobile ? "column" : "row"}
            >
              <InputSelect
                isMulti
                widht="17%"
                widhtMobile="90%"
                title="Fase"
                label={
                  faseToDisplay.length !== 0
                    ? handleAdjustNameFase(faseToDisplay)
                    : "Selecione uma opção"
                }
                isSelected={faseMyOportunityLocal}
                options={optionFase}
                selectedOption={faseMyOportunityLocal}
                onChange={handleFaseChange}
              />

              <InputSelect
                isMulti
                widht="17%"
                widhtMobile="90%"
                title="Origem de conta"
                label={
                  originAccount.length !== 0
                    ? handleAdjustName(originAccount)
                    : "Selecione uma opção"
                }
                isSelected={originAccount}
                options={optionOriginAccountAcronyms}
                selectedOption={originAccount}
                onChange={handleOriginAccountChange}
              />
            </Flex>

            <Flex flexDirection="column" width={isMobile ? "100%" : "90%"}>
              <Text
                fontSize="12px"
                color={theme.text}
                marginLeft="5px"
                height="18px"
              >
                Filtrar por data de criação
              </Text>
              <Flex gap="6px" flexDirection={isMobile ? "column" : "row"}>
                <InputText
                  label=""
                  name="dateStart"
                  placeholder={`De __/__/___`}
                  value={dateStart}
                  onChange={(e) => handleDateStartChange(e.target.value)}
                  maxLength={10}
                  iconSee={false}
                />
                <InputText
                  label=""
                  name="dateEnd"
                  placeholder={`Até __/__/___`}
                  value={dateEnd}
                  onChange={(e) => handleDateEndChange(e.target.value)}
                  maxLength={10}
                  iconSee={false}
                />
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        {/* TABELA/CARDS DE OPORTUNIDADES */}
        <Box
          mt={4}
          bg="white"
          borderRadius="8px"
          w="full"
          minH={{ base: "400px", xl: "330px", "2xl": "620px" }}
          shadow="sm"
        >
          {loading ? (
            <Flex
              w="100%"
              h={{ base: "400px", xl: "330px", "2xl": "620px" }}
              justify="center"
              align="center"
            >
              <Spinner />
            </Flex>
          ) : (
            <Box
              w="full"
              h="fit-content"
              maxH={{ base: "400px", xl: "330px", "2xl": "620px" }}
              overflow="auto"
              borderRadius="8px"
              css={{
                "&::-webkit-scrollbar": {
                  width: "5px",
                  height: "5px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: theme.primary,
                  borderRadius: "3px",
                },
              }}
            >
              {!loading && filteredRows.length === 0 ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  width="98%"
                  border="1px solid"
                  borderColor={theme.disable100}
                  padding="5px"
                  margin="5px"
                  sx={{
                    "@media (min-width: 1200px)": {
                      padding: "8px",
                      width: "99%",
                    },
                    "@media (min-width: 768px)": {
                      width: "70%",
                    },
                    "@media (max-width: 425px)": {
                      width: "90%",
                    },
                  }}
                >
                  {filteredRows.length === 0 && newSearch === "" ? (
                    <Stack spacing={2}>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        fontWeight="bold"
                        color={theme.text}
                      >
                        Pesquise utilizando as seguintes sugestões
                      </Text>
                      <Text fontSize="xs" color={theme.text}>
                        - Selecione um tipo de pesquisa entre: Nome da conta ou
                        Id da oportunidade;
                      </Text>
                      <Text fontSize="xs" color={theme.text}>
                        - Certifique-se de que os dados estejam digitados
                        corretamente;
                      </Text>
                      <Text fontSize="xs" color={theme.text}>
                        - Tente escolher palavras-chave;
                      </Text>
                      <Text fontSize="xs" color={theme.text}>
                        - Use termos mais genéricos, ou seja, com um conceito
                        mais amplo
                      </Text>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      <Text fontSize="xs" fontWeight="bold" color={theme.error}>
                        Sua pesquisa por "{newSearch}" não correspondeu a nenhum
                        resultado.
                      </Text>
                      <Text fontSize="xs" color={theme.text}>
                        Se preferir, pesquise novamente utilizando as seguintes
                        sugestões:
                      </Text>
                      <Text fontSize="xs" color={theme.text}>
                        - Certifique-se de que todas as palavras estejam
                        digitadas corretamente;
                      </Text>
                      <Text fontSize="xs" color={theme.text}>
                        - Tente escolher palavras-chave diferentes das que você
                        digitou;
                      </Text>
                      <Text fontSize="xs" color={theme.text}>
                        - Use termos mais genéricos, ou seja, com um conceito
                        mais amplo.
                      </Text>
                    </Stack>
                  )}
                </Box>
              ) : (
                <TableMyOportunity
                  data={dataOportunity}
                  searchFilter={search}
                  fase={faseMyOportunityLocal}
                  onClick={navigation}
                />
              )}
            </Box>
          )}
        </Box>
      </>
    );
  };

  return <MyOportunityComponentOriginalWrapper navigation={handleNavigation} />;
};

export default React.memo(MyOportunityComponent);
